import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';

export default class VerifyAPI {
  private request: TypeRequest;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }
  /**
   * @param {string} mobile - mobile to be verified
   * @return {Promise} - send result
   */
  notLoginSendCode = (params: { mobile: string; extra_params?: object }) => {
    const { mobile, extra_params = {} } = params;
    const data = encryptParams({ mobile, type: 34, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - mobile to be verified
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  notLoginValidateMobile = (params: {
    mobile: string;
    code: string;
    extra_params?: object;
  }) => {
    const { mobile, code, extra_params = {} } = params;
    const data = encryptParams({ mobile, code, type: 34, ...extra_params }, [
      'mobile',
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.CHECK_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - email to be verified
   * @return {Promise} - send result
   */
  notLoginSendEmailCode = (params: {
    email: string;
    extra_params?: object;
  }) => {
    const { email, extra_params = {} } = params;
    const data = encryptParams({ email, type: 15, ...extra_params }, [
      'email',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - email to be verified
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  notLoginValidateEmail = (params: {
    email: string;
    code: string;
    extra_params?: object;
  }) => {
    const { email, code, extra_params = {} } = params;
    const data = encryptParams({ email, code, type: 15, ...extra_params }, [
      'email',
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.CHECK_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} - send result
   */
  sendCode = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    const data = encryptParams({ type: 22, ...extra_params }, ['type']);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  validateMobile = (params: { code: string; extra_params?: object }) => {
    const { code, extra_params = {} } = params;
    const data = encryptParams({ code, type: 22, ...extra_params }, [
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.VERIFY_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @return {Promise} - send result
   */
  sendEmailCode = (params: { extra_params?: object } = {}) => {
    const { extra_params = {} } = params;
    const data = encryptParams({ type: 6, ...extra_params }, ['type']);
    return this.request({
      url: WEB_URL.SEND_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} code - the captcha
   * @return {Promise} - verify result
   */
  validateEmail = (params: { code: string; extra_params?: object }) => {
    const { code, extra_params = {} } = params;
    const data = encryptParams({ code, type: 6, ...extra_params }, [
      'code',
      'type',
    ]);
    return this.request({
      url: WEB_URL.VERIFY_EMAIL_CODE,
      method: 'POST',
      data,
    });
  };

  /**
   * @param {string} account - account to be verified, mobile / email / username
   * @param {string} password - corresponding password
   * @return {Promise} - verify result
   */
  accountVerify = (params: {
    account: string;
    password: string;
    extra_params?: object;
  }) => {
    const { account, password, extra_params = {} } = params;
    const data = encryptParams(
      { account, password, user_login_version: 1, ...extra_params },
      ['account', 'password']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.ACCOUNT_VERIFY,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {number} platform_app_id - the app id of the third party used to login
   * @param {string} code - authorization code
   * @param {string} access_token - third-party authorization token
   * @param {string} access_token_secret - id token, only neccessary when using google to login
   * @param {string} openid - third-party openid, used with access_token
   * @param {string} profile_key - third-party ticket that preserves information temporaily, used when the login progress is blocked
   * @return {Promise} login result
   */
  authVerify = (params: {
    platform_app_id: number;
    code?: string;
    access_token?: string;
    access_token_secret?: string;
    openid?: string;
    profile_key?: string;
    extra_params?: object;
  }) => {
    const {
      platform_app_id,
      code,
      access_token,
      access_token_secret,
      openid,
      profile_key,
      extra_params = {},
    } = params;
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.AUTH_VERIFY,
        method: 'POST',
        data: {
          platform_app_id,
          code,
          access_token,
          access_token_secret,
          openid,
          profile_key,
          ...extra_params,
        },
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };
  /**
   * @param {string} mobile - the mobile used to authorize
   * @return {Promise} send result
   */
  smsAuthorizeSendCode = (params: {
    mobile: string;
    extra_params?: object;
  }) => {
    const { mobile, extra_params = {} } = params;
    let data = encryptParams({ mobile, type: 26, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };
  /**
   * @param {string} mobile - the mobile used to authorize
   * @param {string} code - the code
   * @return {Promise} send result
   */
  smsAuthorize = (params: {
    mobile: string;
    code: string;
    extra_params?: object;
  }) => {
    const { mobile, code, extra_params = {} } = params;
    let data = encryptParams({ mobile, code, type: 26, ...extra_params }, [
      'mobile',
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SMS_AUTHORIZE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} need_limit_platform
   * @param {string} not_login_ticket
   * @param {string} ticket
   * @return {Promise} request result
   */
  getAvailableWays = (
    params: {
      need_limit_platform?: number;
      not_login_ticket?: string;
      ticket?: string;
      extra_params?: object;
    } = {}
  ) => {
    const {
      need_limit_platform = 0,
      not_login_ticket,
      ticket,
      extra_params = {},
    } = params;
    return this.request({
      url: WEB_URL.AVAILABLE_WAYS,
      method: 'POST',
      data: {
        need_limit_platform,
        not_login_ticket,
        ticket,
        ...extra_params,
      },
    });
  };

  /**
   * @param {string} username
   * @param {string} mobile
   * @param {string} email
   * @param {string} password
   * @param {string} verify_ticket
   * @return {Promise} verify result
   */
  verifyPwdWithoutLogin = (params: {
    username?: string;
    mobile?: string;
    email?: string;
    password: string;
    verify_ticket?: string;
    extra_params?: object;
  }) => {
    const {
      username,
      mobile,
      email,
      password,
      verify_ticket,
      extra_params = {},
    } = params;
    let data = encryptParams(
      { username, mobile, email, password, verify_ticket, ...extra_params },
      ['username', 'mobile', 'email', 'password']
    );
    return this.request({
      url: WEB_URL.VERIFY_PWD_WITHOUT_LOGIN,
      method: 'POST',
      data,
    });
  };
}
